'use client'

import { Box, Paper, useMediaQuery } from '@mui/material'
import { DeviceContext, getDevice, useLoginInfo } from 'app/_hooks'
import * as Retail from 'app/_hooks/useRetail'
import { Auth } from 'aws-amplify'
import { usePathname } from 'next/navigation'
import { FC, ReactNode, useEffect, useMemo } from 'react'
import useSWR from 'swr'
import { AppBottomNavigation } from '~/components/AppBottomNavigation'
import { AppFooter } from '~/components/AppFooter'
import { useAddLocaleParams } from '~/features/i18n/hooks/useAddLocaleParams'
import { PlayListPopOverStartingPointProvider } from '~/features/play_list/components/PlayListPopOverStartingPointProvider'
import { PlayListVideoActionController } from '~/features/play_list/components/PlayListVideoActionController'
import { useAxios } from '~/libs/axios'
import * as GA from '~/libs/ga'
import { theme } from '~/libs/mui/theme'
import { Profile, profileIsDuringFreeTrial } from '~/types/api/users'

type AuthLayoutProps = {
  children?: ReactNode
}

const AuthLayout = ({ children }: { children: ReactNode }) => {
  return (
    <DeviceContext.Provider
      value={typeof navigator === 'undefined' ? 'PC' : getDevice(navigator.userAgent)}
    >
      {/** TODO: 一旦、AuthLayoutにしているが未ログイン対応が入るので将来的には別のレイアウトに吐き出される */}
      <_AuthLayout>{children}</_AuthLayout>
    </DeviceContext.Provider>
  )
}

const _AuthLayout: FC<AuthLayoutProps> = ({ children }) => {
  const axios = useAxios()
  const localeParams = useAddLocaleParams()
  const loginInfo = useLoginInfo()

  const { data: profile, isLoading } = useSWR<Profile>(
    loginInfo.status === 'signedIn' ? `/v2/user/edit?${localeParams}` : null,
    (args: string) => axios.get(args).then((res) => res.data),
  )

  // トークンのセットが完了したらユーザーのログイン状態を更新する
  useEffect(() => {
    if (isLoading) return

    Auth.currentAuthenticatedUser()
      .then((user) => {
        // uBlockなどの広告ブロッカーが有効な場合、DD_RUMが読み込まれないため、チェックしてから使用する
        if (window.DD_RUM && typeof window.DD_RUM.setUser === 'function') {
          window.DD_RUM.setUser({
            cognitoUsername: user?.username,
            cognitoEmail: user?.attributes?.email,
          })
        }
      })
      .catch(() => {})
  }, [isLoading])

  useEffect(() => {
    if (profile != undefined) {
      switch (profile.user_status) {
        case 'free_user':
          GA.setUserType('free')
          break
        case 'subscription_user':
          GA.setUserType(profileIsDuringFreeTrial(profile) ? 'trial' : 'paying')
          break
        case 'withdrawal_user':
          GA.setUserType(
            profileIsDuringFreeTrial(profile)
              ? 'trialCancel'
              : profile.payment_type != 'no_subscription'
              ? 'payingCancel'
              : 'paying',
          )
          break
        default:
          break
      }
      GA.setUserId(profile.id)
      Retail.setUserId(profile.id)
      return
    }
  }, [profile])

  return (
    <Box>
      <PlayListPopOverStartingPointProvider>
        {children}

        <Footer />

        {/* others */}
        <PlayListVideoActionController />
      </PlayListPopOverStartingPointProvider>
    </Box>
  )
}

const Footer: FC = () => {
  const isSm = useMediaQuery(theme.breakpoints.up('sm'))
  const pathname = usePathname()
  const unshowFooter =
    pathname?.startsWith('/webview/profile/new') ||
    pathname?.startsWith('/onboarding') ||
    pathname?.startsWith('/survey_chats/')
  const showMobileFooter = useMemo(() => {
    return !pathname?.startsWith('/videos/')
  }, [pathname])

  if (unshowFooter) return null

  return isSm ? (
    <Box bottom={0} left={0} right={0}>
      <AppFooter />
    </Box>
  ) : showMobileFooter ? (
    <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0 }} elevation={3}>
      <AppBottomNavigation />
    </Paper>
  ) : null
}

export default AuthLayout
